import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useMemo } from 'react';
import debounce from 'debounce';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';

const Autocomplete = ({ query, onSelect, onSearch, debounceMs = 300 }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { selectedIndex, setSelectedIndex, listRef, resetSelection } = useKeyboardNavigation(items, onSelect);

  const searchItems = useCallback(
    async searchQuery => {
      if (!searchQuery) {
        setItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await onSearch(searchQuery);
        setItems(results);
        resetSelection();
      } catch (error) {
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [onSearch, resetSelection],
  );

  const debouncedSearch = useMemo(
    () => debounce(searchQuery => searchItems(searchQuery), debounceMs),
    [searchItems, debounceMs],
  );

  useEffect(() => {
    debouncedSearch(query);
    return () => debouncedSearch.clear();
  }, [query, debouncedSearch]);

  if (!query || items.length === 0) {
    return null;
  }

  return (
    <div className="relative" role="listbox">
      <div className="absolute left-0 right-0 bg-white rounded-b-md border border-gray-200 border-t-0 max-h-60 overflow-y-auto z-50">
        <div className="p-4 space-y-2" ref={listRef}>
          {isLoading ? (
            <div className="text-sm text-gray-500 text-center py-2">Loading...</div>
          ) : (
            items.map((item, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors duration-150 ${
                  index === selectedIndex ? 'bg-gray-100' : ''
                }`}
                onClick={() => {
                  setSelectedIndex(index);
                  onSelect(item);
                }}
                role="option"
                aria-selected={index === selectedIndex}
                tabIndex={-1}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-500">{item.email}</div>
                  </div>
                </div>
                <div className="btn btn-md btn-ghost">Add</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

Autocomplete.propTypes = {
  query: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  debounceMs: PropTypes.number,
};

export default Autocomplete;
